import { DataTable, TruncCell, badgeCapacity } from "@/components/common/DataTable";
import { BadgeOverflow } from "@/components/common/BadgeOverflow";
import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, Wrench, Edit2, X, Save, Star, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Checkbox } from "@/components/ui/checkbox";
import { useIsSuperadmin } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCompanyApplianceCategories } from "@/hooks/useCompanyApplianceCategories";
import { SettingsBackLink } from "@/components/settings/SettingsBackLink";

const FALLBACK_APPLIANCE_TYPES = ["Washing Machine", "Dryer", "Dishwasher", "Oven", "Household Refrigerator"];

interface CatalogPart {
  id: string;
  name: string;
  part_number: string | null;
  brand: string | null;
  appliance_type: string;
  model_pattern: string | null;
  price: number;
  currency: string;
  notes: string | null;
}

interface NewPart {
  name: string;
  part_number: string;
  brand: string;
  appliance_type: string;
  model_pattern: string;
  price: string;
  notes: string;
}

const emptyPart: NewPart = {
  name: "", part_number: "", brand: "", appliance_type: "",
  model_pattern: "", price: "", notes: "",
};

export default function SparePartConfiguration() {
  const { t } = useTranslation("admin");
  const navigate = useNavigate();
  const { toast } = useToast();
  const qc = useQueryClient();
  const { isSuperadmin: isAdmin, isLoading: roleLoading } = useIsSuperadmin();
  const { names: companyApplianceTypes } = useCompanyApplianceCategories();
  const APPLIANCE_TYPES = companyApplianceTypes.length > 0 ? companyApplianceTypes : FALLBACK_APPLIANCE_TYPES;

  const [showNew, setShowNew] = useState(false);
  const [newPart, setNewPart] = useState<NewPart>(emptyPart);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<NewPart>(emptyPart);

  // Required-parts tab filters
  const [reqApplianceType, setReqApplianceType] = useState<string>(APPLIANCE_TYPES[0]);
  const [reqBrand, setReqBrand] = useState<string>("__any__");
  const [reqModelPattern, setReqModelPattern] = useState<string>("");

  // Bindings tab filters
  const [bindApplianceType, setBindApplianceType] = useState<string>(APPLIANCE_TYPES[0]);
  const [bindBrand, setBindBrand] = useState<string>("__any__");

  const { data: brands = [], error: brandsError } = useQuery({
    queryKey: ["brands"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("brands")
        .select("id, name")
        .order("name", { ascending: true });
      if (error) {
        console.error("[SparePartConfiguration] failed to load brands:", error);
        throw error;
      }
      return (data || []) as { id: string; name: string }[];
    },
    staleTime: 60_000,
  });
  if (brandsError) {
    console.warn("Brands load error:", brandsError);
  }

  const { data: catalog = [] } = useQuery({
    queryKey: ["spare-parts-catalog"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("spare_parts_catalog" as any)
        .select("*")
        .order("appliance_type")
        .order("name");
      if (error) throw error;
      return (data || []) as unknown as CatalogPart[];
    },
  });

  const { data: requiredRows = [] } = useQuery({
    queryKey: ["spare-part-required"],
    queryFn: async () => {
      const { data, error } = await supabase.from("spare_part_required" as any).select("*");
      if (error) throw error;
      return (data || []) as unknown as { id: string; catalog_id: string; appliance_type: string; brand: string | null; model_pattern: string | null }[];
    },
  });

  const { data: workflowItems = [] } = useQuery({
    queryKey: ["workflow-items-for-bindings", bindApplianceType, bindBrand],
    queryFn: async () => {
      let q = supabase
        .from("workflow_items")
        .select("id, title, brand, appliance_type, category")
        .eq("appliance_type", bindApplianceType);
      const { data, error } = await q;
      if (error) throw error;
      return (data || []).filter((it: any) => bindBrand === "__any__" || !it.brand || it.brand === bindBrand) as { id: string; title: string; brand: string | null; appliance_type: string; category: string }[];
    },
  });

  const { data: bindingRows = [] } = useQuery({
    queryKey: ["spare-part-workflow-bindings"],
    queryFn: async () => {
      const { data, error } = await supabase.from("spare_part_workflow_bindings" as any).select("*");
      if (error) throw error;
      return (data || []) as unknown as { id: string; catalog_id: string; workflow_item_id: string; quantity: number }[];
    },
  });

  // ===== Mutations =====
  const createPart = useMutation({
    mutationFn: async (p: NewPart) => {
      const payload = {
        name: p.name.trim(),
        part_number: p.part_number.trim() || null,
        brand: p.brand.trim() || null,
        appliance_type: p.appliance_type,
        model_pattern: p.model_pattern.trim() || null,
        price: Number(p.price) || 0,
        notes: p.notes.trim() || null,
      };
      const { error } = await supabase.from("spare_parts_catalog" as any).insert(payload);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["spare-parts-catalog"] });
      setShowNew(false);
      setNewPart(emptyPart);
      toast({ title: t("parts.toast.added") });
    },
    onError: (e: any) => toast({ title: t("parts.toast.failed"), description: e.message, variant: "destructive" }),
  });

  const updatePart = useMutation({
    mutationFn: async ({ id, p }: { id: string; p: NewPart }) => {
      const { error } = await supabase
        .from("spare_parts_catalog" as any)
        .update({
          name: p.name.trim(),
          part_number: p.part_number.trim() || null,
          brand: p.brand.trim() || null,
          appliance_type: p.appliance_type,
          model_pattern: p.model_pattern.trim() || null,
          price: Number(p.price) || 0,
          notes: p.notes.trim() || null,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["spare-parts-catalog"] });
      setEditingId(null);
      toast({ title: t("parts.toast.saved") });
    },
    onError: (e: any) => toast({ title: t("parts.toast.failed"), description: e.message, variant: "destructive" }),
  });

  const deletePart = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("spare_parts_catalog" as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["spare-parts-catalog"] });
      qc.invalidateQueries({ queryKey: ["spare-part-required"] });
      qc.invalidateQueries({ queryKey: ["spare-part-workflow-bindings"] });
      toast({ title: t("parts.toast.deleted") });
    },
  });

  const addRequired = useMutation({
    mutationFn: async (catalogId: string) => {
      const payload = {
        catalog_id: catalogId,
        appliance_type: reqApplianceType,
        brand: reqBrand === "__any__" ? null : reqBrand,
        model_pattern: reqModelPattern.trim() || null,
      };
      const { error } = await supabase.from("spare_part_required" as any).insert(payload);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["spare-part-required"] });
      toast({ title: t("parts.toast.markedRequired") });
    },
    onError: (e: any) => toast({ title: t("parts.toast.failed"), description: e.message, variant: "destructive" }),
  });

  const removeRequired = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("spare_part_required" as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["spare-part-required"] }),
  });

  const addBinding = useMutation({
    mutationFn: async ({ catalogId, workflowItemId, quantity }: { catalogId: string; workflowItemId: string; quantity: number }) => {
      const { error } = await supabase
        .from("spare_part_workflow_bindings" as any)
        .insert({ catalog_id: catalogId, workflow_item_id: workflowItemId, quantity, appliance_type: bindApplianceType });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["spare-part-workflow-bindings"] });
      toast({ title: t("parts.toast.boundToStep") });
    },
    onError: (e: any) => toast({ title: t("parts.toast.failed"), description: e.message, variant: "destructive" }),
  });

  const removeBinding = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("spare_part_workflow_bindings" as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["spare-part-workflow-bindings"] }),
  });

  if (roleLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-background"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }
  // Access enforced by the route guard in App.tsx.

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-8 pb-6">
        <SettingsBackLink to="/platform/data" label={t("nav.data", { ns: "settings" })} />
        <div className="flex items-center gap-2 mb-1">
          <Wrench className="w-5 h-5 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">{t("parts.title")}</h1>
        </div>
        <p className="text-muted-foreground">{t("parts.subtitle")}</p>
      </div>

      <div className="px-6">
        <Tabs defaultValue="catalog">
          <TabsList>
            <TabsTrigger value="catalog">{t("parts.tabs.catalog")}</TabsTrigger>
            <TabsTrigger value="required">{t("parts.tabs.required")}</TabsTrigger>
            <TabsTrigger value="bindings">{t("parts.tabs.bindings")}</TabsTrigger>
          </TabsList>

          {/* ===== CATALOG ===== */}
          <TabsContent value="catalog" className="space-y-4 mt-4">
            <div className="flex justify-end">
              <Button onClick={() => setShowNew(!showNew)} className="gap-2">
                <Plus className="w-4 h-4" /> {t("parts.addPart")}
              </Button>
            </div>

            {showNew && (
              <PartForm
                value={newPart}
                onChange={setNewPart}
                brands={brands}
                applianceTypes={APPLIANCE_TYPES}
                onSave={() => createPart.mutate(newPart)}
                onCancel={() => { setShowNew(false); setNewPart(emptyPart); }}
                saving={createPart.isPending}
              />
            )}

            {editingId && (() => { const p = catalog.find((c) => c.id === editingId); return p ? (
              <div className="bg-muted/20 rounded-xl border border-border p-3 mb-3">
                <PartForm value={editForm} onChange={setEditForm} brands={brands} applianceTypes={APPLIANCE_TYPES}
                  onSave={() => updatePart.mutate({ id: p.id, p: editForm })} onCancel={() => setEditingId(null)} saving={updatePart.isPending} />
              </div>) : null; })()}
            <DataTable<(typeof catalog)[number]>
              storageKey="spare-parts-catalog-column-widths"
              rows={catalog}
              rowKey={(p) => p.id}
              empty={t("parts.empty")}
              columns={[
                { key: "name", header: t("parts.cols.name"), width: 240, sortValue: (p) => p.name.toLowerCase(), cell: (p) => <TruncCell className="font-medium">{p.name}</TruncCell> },
                { key: "part_number", header: t("parts.cols.partNumber"), width: 140, sortValue: (p) => p.part_number, cell: (p) => <TruncCell className="text-muted-foreground font-mono text-xs">{p.part_number || "—"}</TruncCell> },
                { key: "brand", header: t("parts.cols.brand"), width: 120, sortValue: (p) => p.brand, cell: (p) => <TruncCell>{p.brand || "—"}</TruncCell> },
                { key: "appliance", header: t("parts.cols.appliance"), width: 140, sortValue: (p) => p.appliance_type, cell: (p) => <TruncCell>{p.appliance_type}</TruncCell> },
                { key: "model_pattern", header: t("parts.cols.modelPattern"), width: 260, sortValue: (p) => p.model_pattern,
                  cell: (p, { width }) => p.model_pattern
                    ? <BadgeOverflow visibleCount={badgeCapacity(width, 110)} items={p.model_pattern.split(/[,;|]/).map((m) => m.trim()).filter(Boolean).map((m, i) => ({ id: `${i}-${m}`, label: m }))} />
                    : <span className="text-muted-foreground text-sm">{t("parts.allModels")}</span> },
                { key: "price", header: t("parts.cols.price"), width: 100, align: "right", sortValue: (p) => Number(p.price), cell: (p) => <span className="tabular-nums">€ {Number(p.price).toFixed(2)}</span> },
              ]}
              actionsWidth={96}
              actions={(p) => (
                <>
                  <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Edit" onClick={() => {
                    setEditingId(p.id);
                    setEditForm({
                      name: p.name, part_number: p.part_number || "", brand: p.brand || "",
                      appliance_type: p.appliance_type, model_pattern: p.model_pattern || "",
                      price: String(p.price), notes: p.notes || "",
                    });
                  }}><Edit2 className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Delete" onClick={() => { if (confirm(t("parts.deleteConfirm"))) deletePart.mutate(p.id); }}>
                    <Trash2 className="w-3.5 h-3.5 text-destructive" />
                  </Button>
                </>
              )}
            />
          </TabsContent>

          {/* ===== REQUIRED PER MODEL ===== */}
          <TabsContent value="required" className="space-y-4 mt-4">
            <div className="bg-card rounded-xl border border-border p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium"><Star className="w-4 h-4 text-primary" /> {t("parts.required.heading")}</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">{t("parts.required.applianceType")}</label>
                  <Select value={reqApplianceType} onValueChange={setReqApplianceType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{APPLIANCE_TYPES.map((at) => <SelectItem key={at} value={at}>{at}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">{t("parts.required.brand")}</label>
                  <SearchableSelect
                    value={reqBrand}
                    onChange={(v) => setReqBrand(v ?? "__any__")}
                    placeholder={t("parts.anyBrand")}
                    searchPlaceholder={t("parts.searchBrands")}
                    emptyMessage={t("parts.noBrandsFound")}
                    items={[{ value: "__any__", label: t("parts.anyBrand") }, ...brands.map((b) => ({ value: b.name, label: b.name }))]}
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">{t("parts.required.modelPatternLabel")}</label>
                  <Input value={reqModelPattern} onChange={(e) => setReqModelPattern(e.target.value)} placeholder={t("parts.required.modelPatternPlaceholder")} />
                </div>
              </div>
              <p className="text-xs text-muted-foreground" dangerouslySetInnerHTML={{ __html: t("parts.required.helper") }} />
            </div>

            <div className="bg-card rounded-xl border border-border overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/40">
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="px-4 py-2">{t("parts.cols.catalogPart")}</th>
                    <th className="px-4 py-2">{t("parts.cols.brandType")}</th>
                    <th className="px-4 py-2 text-right">{t("parts.cols.price")}</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {catalog.filter((c) => c.appliance_type === reqApplianceType).map((p) => (
                    <tr key={p.id} className="border-t border-border text-sm">
                      <td className="px-4 py-2 font-medium">{p.name}</td>
                      <td className="px-4 py-2 text-muted-foreground">{p.brand || t("parts.any")} · {p.appliance_type}</td>
                      <td className="px-4 py-2 text-right">€ {Number(p.price).toFixed(2)}</td>
                      <td className="px-4 py-2 text-right">
                        <Button size="sm" variant="outline" onClick={() => addRequired.mutate(p.id)}>
                          <Star className="w-3.5 h-3.5 mr-1" /> {t("parts.required.markRequired")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-2 mt-6">{t("parts.required.existingTitle")}</h3>
              <div className="bg-card rounded-xl border border-border overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted/40">
                    <tr className="text-left text-xs text-muted-foreground">
                      <th className="px-4 py-2">{t("parts.cols.part")}</th>
                      <th className="px-4 py-2">{t("parts.cols.appliance")}</th>
                      <th className="px-4 py-2">{t("parts.cols.brand")}</th>
                      <th className="px-4 py-2">{t("parts.cols.modelPattern")}</th>
                      <th className="px-4 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {requiredRows.length === 0 && (
                      <tr><td colSpan={5} className="px-4 py-6 text-center text-sm text-muted-foreground">{t("parts.required.noRules")}</td></tr>
                    )}
                    {requiredRows.map((r) => {
                      const part = catalog.find((c) => c.id === r.catalog_id);
                      return (
                        <tr key={r.id} className="border-t border-border text-sm">
                          <td className="px-4 py-2 font-medium">{part?.name || t("parts.deleted")}</td>
                          <td className="px-4 py-2">{r.appliance_type}</td>
                          <td className="px-4 py-2">{r.brand || t("parts.any")}</td>
                          <td className="px-4 py-2 text-muted-foreground">{r.model_pattern || t("parts.any")}</td>
                          <td className="px-4 py-2 text-right">
                            <Button variant="ghost" size="sm" onClick={() => removeRequired.mutate(r.id)}>
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* ===== WORKFLOW STEP BINDINGS ===== */}
          <TabsContent value="bindings" className="space-y-4 mt-4">
            <div className="bg-card rounded-xl border border-border p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium"><Link2 className="w-4 h-4 text-primary" /> {t("parts.bindings.heading")}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">{t("parts.bindings.applianceType")}</label>
                  <Select value={bindApplianceType} onValueChange={setBindApplianceType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{APPLIANCE_TYPES.map((at) => <SelectItem key={at} value={at}>{at}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">{t("parts.bindings.brandFilter")}</label>
                  <SearchableSelect
                    value={bindBrand}
                    onChange={(v) => setBindBrand(v ?? "__any__")}
                    placeholder={t("parts.anyBrand")}
                    searchPlaceholder={t("parts.searchBrands")}
                    emptyMessage={t("parts.noBrandsFound")}
                    items={[{ value: "__any__", label: t("parts.anyBrand") }, ...brands.map((b) => ({ value: b.name, label: b.name }))]}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {workflowItems.length === 0 && (
                <p className="text-sm text-muted-foreground">{t("parts.bindings.noSteps")}</p>
              )}
              {workflowItems.map((it) => {
                const itemBindings = bindingRows.filter((b) => b.workflow_item_id === it.id);
                return (
                  <div key={it.id} className="bg-card rounded-xl border border-border p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium text-foreground">{it.title}</p>
                        <p className="text-xs text-muted-foreground">{it.category} · {it.brand || t("parts.bindings.anyBrand")}</p>
                      </div>
                      <BindPartPicker
                        catalog={catalog.filter((c) => c.appliance_type === bindApplianceType)}
                        onAdd={(catalogId, qty) => addBinding.mutate({ catalogId, workflowItemId: it.id, quantity: qty })}
                      />
                    </div>
                    {itemBindings.length > 0 && (
                      <div className="mt-3 space-y-1">
                        {itemBindings.map((b) => {
                          const part = catalog.find((c) => c.id === b.catalog_id);
                          return (
                            <div key={b.id} className="flex items-center justify-between text-sm bg-muted/30 rounded px-3 py-1.5">
                              <span>{part?.name || t("parts.deleted")} · {t("parts.bindings.qty", { count: b.quantity })}</span>
                              <div className="flex items-center gap-3">
                                <span className="text-muted-foreground">€ {((part?.price || 0) * b.quantity).toFixed(2)}</span>
                                <Button variant="ghost" size="sm" onClick={() => removeBinding.mutate(b.id)}>
                                  <X className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ===== Sub-components =====

function PartForm({
  value, onChange, brands, applianceTypes, onSave, onCancel, saving,
}: {
  value: NewPart;
  onChange: (v: NewPart) => void;
  brands: { name: string }[];
  applianceTypes: string[];
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const { t } = useTranslation("admin");
  const applianceValid = !!value.appliance_type;
  return (
    <div className="bg-muted/20 rounded-lg p-4 space-y-3 border border-border">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-muted-foreground">{t("parts.form.nameLabel")}</label>
          <Input value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} placeholder={t("parts.form.namePlaceholder")} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">{t("parts.form.partNumber")}</label>
          <Input value={value.part_number} onChange={(e) => onChange({ ...value, part_number: e.target.value })} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">{t("parts.form.brand")}</label>
          <Select value={value.brand || "__none__"} onValueChange={(v) => onChange({ ...value, brand: v === "__none__" ? "" : v })}>
            <SelectTrigger><SelectValue placeholder={t("parts.any")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">{t("parts.form.anyBrand")}</SelectItem>
              {brands.map((b) => <SelectItem key={b.name} value={b.name}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">
            {t("parts.form.applianceType")} <span className="text-destructive">*</span>
          </label>
          <Select value={value.appliance_type || undefined} onValueChange={(v) => onChange({ ...value, appliance_type: v })}>
            <SelectTrigger><SelectValue placeholder="Select appliance type" /></SelectTrigger>
            <SelectContent>{applianceTypes.map((at) => <SelectItem key={at} value={at}>{at}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">{t("parts.form.modelPattern")}</label>
          <Input value={value.model_pattern} onChange={(e) => onChange({ ...value, model_pattern: e.target.value })} placeholder={t("parts.form.modelPatternPlaceholder")} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">{t("parts.form.price")}</label>
          <Input type="number" step="0.01" value={value.price} onChange={(e) => onChange({ ...value, price: e.target.value })} />
        </div>
      </div>
      <div>
        <label className="text-xs text-muted-foreground">{t("parts.form.notes")}</label>
        <Textarea value={value.notes} onChange={(e) => onChange({ ...value, notes: e.target.value })} rows={2} />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>{t("parts.form.cancel")}</Button>
        <Button onClick={onSave} disabled={saving || !value.name.trim() || !applianceValid}><Save className="w-4 h-4 mr-1" /> {t("parts.form.save")}</Button>
      </div>
    </div>
  );
}

function BindPartPicker({ catalog, onAdd }: { catalog: CatalogPart[]; onAdd: (catalogId: string, quantity: number) => void }) {
  const { t } = useTranslation("admin");
  const [partId, setPartId] = useState<string>("");
  const [qty, setQty] = useState<string>("1");
  return (
    <div className="flex items-center gap-2">
      <Select value={partId} onValueChange={setPartId}>
        <SelectTrigger className="w-56"><SelectValue placeholder={t("parts.picker.selectPart")} /></SelectTrigger>
        <SelectContent>
          {catalog.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} (€{Number(p.price).toFixed(2)})</SelectItem>)}
        </SelectContent>
      </Select>
      <Input type="number" min="1" step="1" value={qty} onChange={(e) => setQty(e.target.value)} className="w-20" />
      <Button size="sm" disabled={!partId} onClick={() => { onAdd(partId, Number(qty) || 1); setPartId(""); setQty("1"); }}>
        <Plus className="w-4 h-4 mr-1" /> {t("parts.picker.bind")}
      </Button>
    </div>
  );
}
